import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import appConfig from '../config';

const SUGGESTIONS = [
    'Что без глютена?',
    'Есть вегетарианские блюда?',
    'Что посоветуете на ужин?',
    'Самое популярное блюдо?',
];

const MenuChatWidget = ({ restaurantId, compact = false }) => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: 'bot', text: 'Привет! Спросите меня про меню — помогу выбрать.' },
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
            setTimeout(() => inputRef.current?.focus(), 300);
        }
    }, [open, messages]);

    const send = async (question) => {
        const q = (question || input).trim();
        if (!q || loading) return;

        const history = messages
            .filter(m => m.role !== 'bot' || messages.indexOf(m) > 0)
            .slice(-6)
            .map(m => ({ role: m.role === 'bot' ? 'assistant' : 'user', content: m.text }));

        setMessages(prev => [...prev, { role: 'user', text: q }]);
        setInput('');
        setLoading(true);

        try {
            const res = await appConfig.authFetch(`${appConfig.endpoint}/menu-chat`, {
                method: 'POST',
                body: JSON.stringify({ question: q, restaurant_id: restaurantId, history }),
            });
            const data = await res.json();
            setMessages(prev => [...prev, {
                role: 'bot',
                text: data.success ? data.answer : 'Не удалось получить ответ. Попробуйте ещё раз.',
            }]);
        } catch {
            setMessages(prev => [...prev, {
                role: 'bot',
                text: 'Ошибка соединения. Попробуйте позже.',
            }]);
        } finally {
            setLoading(false);
        }
    };

    const showSuggestions = messages.length <= 1 && !loading;

    return (
        <>
            {compact ? (
                <button
                    onClick={() => setOpen(true)}
                    className="w-full flex items-center gap-2.5 bg-violet-50 border border-violet-200 rounded-2xl px-3 py-3 active:scale-[0.97] transition-transform duration-150"
                >
                    <div className="w-8 h-8 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs font-semibold text-gray-900 leading-tight truncate">Спросить бота</p>
                        <p className="text-[11px] text-violet-600 mt-0.5 font-medium">AI-помощник</p>
                    </div>
                </button>
            ) : (
                <button
                    onClick={() => setOpen(true)}
                    className="w-full flex items-center gap-3.5 bg-violet-50 border border-violet-200 rounded-2xl px-4 py-3 active:scale-[0.98] transition-transform duration-150 text-left"
                >
                    <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 leading-tight">Спросить бота</p>
                        <p className="text-xs text-violet-600 mt-0.5 font-medium">AI-помощник по меню</p>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                        <polyline points="9 18 15 12 9 6"/>
                    </svg>
                </button>
            )}

            {open && (
                <div className="fixed inset-0 z-50 flex flex-col justify-end">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
                    <div className="relative bg-white rounded-t-3xl flex flex-col" style={{ maxHeight: '82vh' }}>

                        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 shrink-0">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-xl bg-violet-100 flex items-center justify-center">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">Помощник по меню</p>
                                    <p className="text-xs text-gray-400">Отвечает на основе нашего меню</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
                            {messages.map((msg, i) => (
                                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                                        msg.role === 'user'
                                            ? 'bg-violet-600 text-white rounded-br-sm'
                                            : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                                    }`}>
                                        {msg.role === 'user' ? msg.text : (
                                            <ReactMarkdown
                                                components={{
                                                    p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                                                    strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                                                    em: ({ children }) => <em className="italic">{children}</em>,
                                                    ul: ({ children }) => <ul className="mt-1 mb-1 space-y-0.5 pl-1">{children}</ul>,
                                                    ol: ({ children }) => <ol className="mt-1 mb-1 space-y-0.5 pl-4 list-decimal">{children}</ol>,
                                                    li: ({ children }) => (
                                                        <li className="flex gap-1.5 items-start">
                                                            <span className="mt-2 w-1 h-1 rounded-full bg-violet-400 shrink-0" />
                                                            <span>{children}</span>
                                                        </li>
                                                    ),
                                                }}
                                            >
                                                {msg.text}
                                            </ReactMarkdown>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {loading && (
                                <div className="flex justify-start">
                                    <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1.5 items-center">
                                        {[0, 1, 2].map(i => (
                                            <div
                                                key={i}
                                                className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"
                                                style={{ animationDelay: `${i * 0.15}s` }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div ref={bottomRef} />
                        </div>

                        {showSuggestions && (
                            <div className="px-4 py-2 flex gap-2 overflow-x-auto shrink-0 scrollbar-hide">
                                {SUGGESTIONS.map((s, i) => (
                                    <button
                                        key={i}
                                        onClick={() => send(s)}
                                        className="shrink-0 text-xs px-3 py-1.5 rounded-full border border-violet-200 text-violet-700 bg-violet-50 whitespace-nowrap"
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="px-4 py-3 border-t border-gray-100 flex gap-2 shrink-0">
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && send()}
                                placeholder="Спросите про меню..."
                                className="flex-1 bg-gray-100 rounded-xl px-4 py-2.5 text-sm outline-none placeholder-gray-400"
                            />
                            <button
                                onClick={() => send()}
                                disabled={!input.trim() || loading}
                                className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center shrink-0 disabled:opacity-40 active:scale-95 transition-transform"
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="22" y1="2" x2="11" y2="13"/>
                                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                                </svg>
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </>
    );
};

export default MenuChatWidget;
